import React, { useMemo } from 'react';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import { CustomerOption, NewCloseData, ControlVatReportRecord } from './types';
import { formatNumber } from './utils';
import { ReadonlyPeriodField, ReadonlyYearField } from './CloseContextSummary';

interface Step3VatReportProps {
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  setNewCloseData: (data: NewCloseData) => void;
  vatReportData: ControlVatReportRecord[];
  vatReportSummary: ControlVatReportRecord | null;
  loadingVatReport: boolean;
  onBack?: () => void;
  onContinue?: () => void;
  loading?: boolean;
  onRefresh?: () => void;
}

const Step3VatReport: React.FC<Step3VatReportProps> = ({
  customers,
  newCloseData,
  setNewCloseData,
  vatReportData,
  vatReportSummary,
  loadingVatReport,
  onBack,
  onContinue,
  loading = false,
  onRefresh,
}) => {
  const customerOptions = customers.map(customer => ({
    value: customer.Oid,
    label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
  }));

  const vatReportSumKeys = [
    'CityTaxSaleReceipt',
    'VatSaleReceipt',
    'VatSaleReport',
    'VatSaleDiff',
    'NotVatSaleReceipt',
    'NotVatSaleReport',
    'NotVatSaleDiff',
    'VatPurchaseReceipt',
    'VatPurchaseReport',
    'VatPurchaseDiff',
    'NotVatPurchaseReceipt',
    'NotVatPurchaseReport',
    'NotVatPurchaseImportReport',
    'NotVatPurchaseBalanceReport',
    'NotVatPurchaseReportTotal',
    'NotVatPurchaseDiffTotal',
    'TT-03A_1',
    'TT-03A_2',
    'TT-03A_3',
    'TT-03A_4',
    'TT-03A_8'
  ];

  const vatReportSummaryRow = useMemo(() => {
    if (vatReportSummary) {
      return vatReportSummary;
    }
    if (!vatReportData.length) {
      return null;
    }

    const sums: any = {
      CustomerOid: newCloseData.CustomerOid,
      YearType: Number(newCloseData.YearType || 0),
      PeriodType: 0
    };

    vatReportSumKeys.forEach((key) => {
      sums[key] = 0;
    });

    vatReportData.forEach((row) => {
      vatReportSumKeys.forEach((key) => {
        const value = Number((row as any)[key]);
        if (!Number.isNaN(value)) {
          sums[key] += value;
        }
      });
    });

    return sums as ControlVatReportRecord;
  }, [vatReportSummary, vatReportData, newCloseData.CustomerOid, newCloseData.YearType]);

  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Харилцагч <span className="text-red-500">*</span>
          </label>
        </div>
        <div className="flex gap-3 ml-4">
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Буцах
            </button>
          )}
          {onContinue && (
            <button
              onClick={onContinue}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={loading || loadingVatReport}
            >
              {loading || loadingVatReport ? 'Ажиллаж байна...' : 'Үргэлжлүүлэх'}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <SearchableDropdown
            options={customerOptions}
            value={newCloseData.CustomerOid}
            onChange={(value) => setNewCloseData({ ...newCloseData, CustomerOid: value })}
            valueLabel={customers.find(c => c.Oid === newCloseData.CustomerOid) 
              ? `${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === newCloseData.CustomerOid)?.CustomerID || ''})`
              : ''}
            placeholder="Харилцагч сонгох"
            className="w-full"
            enableAutoComplete={true}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReadonlyYearField yearType={newCloseData.YearType} />
          <ReadonlyPeriodField periodType={newCloseData.PeriodType} />
        </div>
      </div>

      {/* VAT Control Report Section */}
      <div className="mt-6 border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">НӨАТ-ийн хяналтын тайлан</h3>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loadingVatReport || !newCloseData.CustomerOid || !newCloseData.YearType}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              title="Мэдээлэл шинэчлэх"
            >
              <svg 
                className={`w-4 h-4 ${loadingVatReport ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              Мэдээлэл шинэчлэх
            </button>
          )}
        </div>
        
        {loadingVatReport ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">НӨАТ-ийн хяналтын тайлан уншиж байна...</p>
          </div>
        ) : vatReportData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Сар</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НХАТ (Баримт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ Бор (Баримт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ Бор (Тайлан)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Зөрүү</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй Бор (Баримт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй Бор (Тайлан)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Зөрүү</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ ХА (Баримт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ ХА (Тайлан)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Зөрүү</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Баримт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Тайлан)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Импорт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Баланст)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Тайлангийн нийт)</th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">НӨАТ-гүй ХА (Зөрүү)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vatReportData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-2 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{row.PeriodType}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CityTaxSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.VatSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.VatSaleReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(row.VatSaleDiff || 0) !== 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                      {formatNumber(row.VatSaleDiff)}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatSaleReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(row.NotVatSaleDiff || 0) !== 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                      {formatNumber(row.NotVatSaleDiff)}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.VatPurchaseReceipt)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.VatPurchaseReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(row.VatPurchaseDiff || 0) !== 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                      {formatNumber(row.VatPurchaseDiff)}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatPurchaseReceipt)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatPurchaseReport)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatPurchaseImportReport)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatPurchaseBalanceReport)}</td>
                    <td className="px-2 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.NotVatPurchaseReportTotal)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(row.NotVatPurchaseDiffTotal || 0) !== 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                      {formatNumber(row.NotVatPurchaseDiffTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {vatReportSummaryRow && (
                <tfoot className="bg-gray-50">
                  <tr className="font-semibold text-gray-900">
                    <td className="px-2 py-2 whitespace-nowrap border border-gray-300">Нийт</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.CityTaxSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.VatSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.VatSaleReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(vatReportSummaryRow.VatSaleDiff || 0) !== 0 ? 'text-red-600' : ''}`}>
                      {formatNumber(vatReportSummaryRow.VatSaleDiff)}
                    </td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatSaleReceipt)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatSaleReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(vatReportSummaryRow.NotVatSaleDiff || 0) !== 0 ? 'text-red-600' : ''}`}>
                      {formatNumber(vatReportSummaryRow.NotVatSaleDiff)}
                    </td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.VatPurchaseReceipt)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.VatPurchaseReport)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(vatReportSummaryRow.VatPurchaseDiff || 0) !== 0 ? 'text-red-600' : ''}`}>
                      {formatNumber(vatReportSummaryRow.VatPurchaseDiff)}
                    </td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatPurchaseReceipt)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatPurchaseReport)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatPurchaseImportReport)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatPurchaseBalanceReport)}</td>
                    <td className="px-2 py-2 text-right border border-gray-300">{formatNumber(vatReportSummaryRow.NotVatPurchaseReportTotal)}</td>
                    <td className={`px-2 py-2 text-right border border-gray-300 ${(vatReportSummaryRow.NotVatPurchaseDiffTotal || 0) !== 0 ? 'text-red-600' : ''}`}>
                      {formatNumber(vatReportSummaryRow.NotVatPurchaseDiffTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            НӨАТ-ийн хяналтын тайлангийн мэдээлэл олдсонгүй
          </div>
        )}
      </div>
    </>
  );
};

export default Step3VatReport;
